import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import { toast } from "react-toastify";
import swal from "sweetalert";

const TERMS_DAYS = {
  "Net 30": 30,
  "Net 15": 15,
  "Due on receipt": 0,
};

export default function QuickBookInvoiceAdd() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  const [formData, setFormData] = useState({
    CustomerRef: "",
    BillEmail: "",
    BillAddr: "",
    ShipAddr: "",
    billingAddress: {
      bill_line1: "",
      bill_line2: "",
      bill_city: "",
      bill_country: "",
      bill_postal_code: "",
    },
    shippingAddress: {
      ship_line1: "",
      ship_line2: "",
      ship_city: "",
      ship_country: "",
      ship_postal_code: "",
    },
    ShipVia: "",
    ShipDate: "",
    TrackingNum: "",
    DocNumber: "",
    EstimateNumber: "",
    InvoiceNumber: "",
    SalesTermRef: "Net 30",
    TxnDate: new Date().toISOString().split("T")[0],
    DueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    Line: [
      {
        DetailType: "SalesItemLineDetail",
        SalesItemLineDetail: {
          ItemRef: { value: "", name: "" },
          UnitPrice: 0,
          Qty: 1,
        },
        Description: "",
        Amount: 0,
      },
    ],
    CustomerMemo: "Thank you for your business.",
    PrivateNote: "",
    InternalNotes: "",
    TotalAmt: 0,
    discount_value: 0,
    discount_type: "fixed",
    total_tax: 0,
    total_shipping: 0,
    deposit_percent: 0,
  });

  const fetchInitialData = async () => {
    loader(true);
    try {
      const custRes = await ApiClient.post("quickbook/customers", {
        quickbook_token: user?.quick_book_accesToken || "",
        company: user?.quick_book_companyId,
      });
      if (custRes?.success) setCustomers(custRes?.data || []);

      const prodRes = await ApiClient.post("quickbook/items", {
        quickbook_token: user?.quick_book_accesToken || "",
        company: user?.quick_book_companyId,
      });
      if (prodRes?.success) setProducts(prodRes?.data || []);
    } catch (err) {
      console.error(err);
    }
    loader(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Recalculate DueDate when TxnDate or SalesTermRef changes
  useEffect(() => {
    const days = TERMS_DAYS[formData.SalesTermRef];
    if (days === undefined) return;
    if (!formData.TxnDate) return;
    const base = new Date(formData.TxnDate);
    base.setDate(base.getDate() + days);
    setFormData((prev) => ({
      ...prev,
      DueDate: base.toISOString().split("T")[0],
    }));
  }, [formData.TxnDate, formData.SalesTermRef]);

  // Derived totals
  const subtotal = formData.TotalAmt;
  const discountAmt =
    formData.discount_type === "percent"
      ? (subtotal * Number(formData.discount_value)) / 100
      : Number(formData.discount_value) || 0;
  const taxAmt = Number(formData.total_tax) || 0;
  const shippingAmt = Number(formData.total_shipping) || 0;
  const invoiceTotal = subtotal - discountAmt + taxAmt + shippingAmt;
  const depositAmt = (invoiceTotal * Number(formData.deposit_percent)) / 100;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.Line];
    if (field === "ItemRef") {
      const product = products.find((p) => p.Id === value);
      if (!product) return;
      newLines[index].SalesItemLineDetail.ItemRef = {
        value: product.Id,
        name: product.Name,
      };
      newLines[index].SalesItemLineDetail.UnitPrice = product.UnitPrice || 0;
      newLines[index].Description = product.Description || "";
    } else if (field === "Qty") {
      newLines[index].SalesItemLineDetail.Qty = Number(value);
    } else if (field === "UnitPrice") {
      newLines[index].SalesItemLineDetail.UnitPrice = Number(value);
    } else {
      newLines[index][field] = value;
    }

    newLines[index].Amount =
      newLines[index].SalesItemLineDetail.Qty *
      newLines[index].SalesItemLineDetail.UnitPrice;

    const total = newLines.reduce((acc, line) => acc + line.Amount, 0);
    setFormData((prev) => ({ ...prev, Line: newLines, TotalAmt: total }));
  };

  const addLine = () => {
    setFormData((prev) => ({
      ...prev,
      Line: [
        ...prev.Line,
        {
          DetailType: "SalesItemLineDetail",
          SalesItemLineDetail: {
            ItemRef: { value: "", name: "" },
            UnitPrice: 0,
            Qty: 1,
          },
          Description: "",
          Amount: 0,
        },
      ],
    }));
  };

  const removeLine = (index) => {
    const newLines = formData.Line.filter((_, i) => i !== index);
    const total = newLines.reduce((acc, line) => acc + line.Amount, 0);
    setFormData((prev) => ({ ...prev, Line: newLines, TotalAmt: total }));
  };

  const showError = (msg) =>
    swal({
      icon: "error",
      title: "Validation Error",
      text: msg,
      confirmButtonColor: "#115B9D",
      confirmButtonText: "OK",
    });

  const validate = () => {
    if (!formData.CustomerRef) {
      showError("Please select a customer.");
      return false;
    }
    if (!formData.BillEmail) {
      showError("Customer email is required.");
      return false;
    }
    if (!formData.TxnDate) {
      showError("Invoice date is required.");
      return false;
    }
    if (!formData.DueDate) {
      showError("Due date is required.");
      return false;
    }
    if (formData.Line.length === 0) {
      showError("Please add at least one line item.");
      return false;
    }
    for (let i = 0; i < formData.Line.length; i++) {
      const line = formData.Line[i];
      if (!line.SalesItemLineDetail.ItemRef.value) {
        showError(`Please select a product for line ${i + 1}.`);
        return false;
      }
      if (
        !line.SalesItemLineDetail.UnitPrice ||
        line.SalesItemLineDetail.UnitPrice <= 0
      ) {
        showError(`Please enter a rate greater than 0 for line ${i + 1}.`);
        return false;
      }
      if (!line.SalesItemLineDetail.Qty || line.SalesItemLineDetail.Qty <= 0) {
        showError(`Qty must be greater than 0 for line ${i + 1}.`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const Line = formData.Line.map((l) => {
        const itemLine = {
          Amount: l.Amount,
          DetailType: "SalesItemLineDetail",
          Description: l.Description || "",
          SalesItemLineDetail: {
            ItemRef: {
              value: l.SalesItemLineDetail.ItemRef.value,
            },
            Qty: l.SalesItemLineDetail.Qty,
            UnitPrice: l.SalesItemLineDetail.UnitPrice,
            TaxCodeRef: { value: "TAX" }
          },
        };

        if (formData.EstimateNumber) {
          itemLine.LinkedTxn = [
            {
              TxnId: formData.EstimateNumber,
              TxnType: "Estimate"
            }
          ];
        }

        return itemLine;
      });

      const discountValue = Number(formData.discount_value);
      if (discountValue > 0) {
        Line.push({
          DetailType: "DiscountLineDetail",
          Amount: discountValue,
          DiscountLineDetail: {
            PercentBased: formData.discount_type === "percent"
          }
        });
      }

      const payload = {
        quickbook_token: user?.quick_book_accesToken || "",
        company: user?.quick_book_companyId,
        CustomerRef: { value: formData?.CustomerRef },
        TxnDate: formData.TxnDate,
        DueDate: formData.DueDate,
        BillEmail: { Address: formData.BillEmail },
        BillAddr: {
          Line1: formData.billingAddress?.bill_line1 || "",
          City: formData.billingAddress?.bill_city || "",
          PostalCode: formData.billingAddress?.bill_postal_code || "",
          Country: formData.billingAddress?.bill_country || "",
        },
        ShipAddr: {
          Line1: formData.shippingAddress?.ship_line1 || "",
          City: formData.shippingAddress?.ship_city || "",
          PostalCode: formData.shippingAddress?.ship_postal_code || "",
          Country: formData.shippingAddress?.ship_country || "",
        },
        Line,
        CustomerMemo: { value: formData.CustomerMemo },
        PrivateNote: formData.PrivateNote,
        TxnTaxDetail: {
          TxnTaxCodeRef: { value: "TAX" },
        },
        EmailStatus: "NeedToSend",
      };

      loader(true);
      const res = await ApiClient.post("quickbook/create/invoice", payload);
      if (res?.success) {
        toast.success("Invoice created successfully");
        navigate("/quickbook/invoices");
      } else {
        toast.error(res?.message || "Failed to create invoice");
      }
    } catch (err) {
      toast.error("An error occurred");
    }
    loader(false);
  };

  const extensionsAllow = ["doc", "pdf"];

  const uploadFile = (e) => {
    let files = e.target.files;
    let file = files?.item(0);

    if (!file) return;
    const fileExtension = file?.name?.split(".").pop().toLowerCase();
    let isAllow = extensionsAllow.includes(fileExtension);
    if (!isAllow) {
      toast.error(
        "Please upload a valid file. Accepted formats include document files in DOC and PDF formats.",
      );
      return;
    }
    let url = "upload/document";
    loader(true);
    ApiClient.postFormFileData(url, { file }).then((res) => {
      if (res.success) {
        setFormData({ ...formData, file: res?.fileName });
      }
      loader(false);
    });
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto p-4 max-w-7xl">
        <div className="bg-white shadow-lg rounded-2xl p-8 border border-gray-100">
          <div className="flex justify-between items-center mb-8 border-b pb-6">
            <h1 className="text-2xl font-bold text-[#115B9D]">INVOICE</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Top Section: Customer and Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              <div className="md:col-span-1">
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Customer
                </label>
                <div className="flex items-center gap-2">
                  <select
                    name="CustomerRef"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 transition-all transition-all"
                    value={formData.CustomerRef}
                    onChange={(e) => {
                      const cust = customers.find(
                        (c) => c.Id === e.target.value,
                      );
                      const custBillAddr = cust
                        ? [
                          cust.BillAddr?.Line1,
                          cust.BillAddr?.Line2,
                          cust.BillAddr?.City,
                          cust.BillAddr?.Country,
                          cust.BillAddr?.PostalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")
                        : "";
                      const custShipAddr = cust
                        ? [
                          cust.ShipAddr?.Line1,
                          cust.ShipAddr?.Line2,
                          cust.ShipAddr?.City,
                          cust.ShipAddr?.Country,
                          cust.ShipAddr?.PostalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")
                        : "";
                      setFormData((prev) => ({
                        ...prev,
                        CustomerRef: e.target.value,
                        BillEmail: cust?.PrimaryEmailAddr?.Address || "",
                        BillAddr: custBillAddr,
                        ShipAddr: custShipAddr,
                        billingAddress: {
                          bill_line1: cust?.BillAddr?.Line1 || "",
                          bill_line2: cust?.BillAddr?.Line2 || "",
                          bill_city: cust?.BillAddr?.City || "",
                          bill_country: cust?.BillAddr?.Country || "",
                          bill_postal_code: cust?.BillAddr?.PostalCode || "",
                        },
                        shippingAddress: {
                          ship_line1: cust?.ShipAddr?.Line1 || "",
                          ship_line2: cust?.ShipAddr?.Line2 || "",
                          ship_city: cust?.ShipAddr?.City || "",
                          ship_country: cust?.ShipAddr?.Country || "",
                          ship_postal_code: cust?.ShipAddr?.PostalCode || "",
                        },
                      }));
                    }}
                    required
                  >
                    <option value="">Select Customer</option>
                    {customers.map((c) => (
                      <option key={c.Id} value={c.Id}>
                        {c.DisplayName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="md:col-span-1">
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Customer Email
                </label>
                <input
                  type="email"
                  name="BillEmail"
                  className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 transition-all"
                  value={formData.BillEmail}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Addresses and Dates Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 text-gray-600">
                    Bill to
                  </label>
                  <textarea
                    name="BillAddr"
                    rows="4"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.BillAddr}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 text-gray-600">
                    Ship to
                  </label>
                  <textarea
                    name="ShipAddr"
                    rows="4"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.ShipAddr}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 text-gray-600">
                    Ship from (hidden)
                  </label>
                  <textarea
                    rows="4"
                    readOnly
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none transition-all border-gray-300"
                    value="2000 Avenue P Ste 9\nRiviera Beach, FL 33404"
                  />
                </div>
              </div>

              <div className="space-y-4 bg-gray-50/50 rounded-2xl p-5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Ship via
                    </label>
                    <input
                      type="text"
                      name="ShipVia"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 transition-all"
                      value={formData.ShipVia}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Invoice no.
                    </label>
                    <input
                      type="text"
                      name="DocNumber"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 transition-all"
                      value={formData.DocNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Shipping date
                    </label>
                    <input
                      type="date"
                      name="ShipDate"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 transition-all"
                      value={formData.ShipDate}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Terms
                    </label>
                    <select
                      name="SalesTermRef"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 transition-all"
                      value={formData.SalesTermRef}
                      onChange={handleInputChange}
                    >
                      <option>Net 30</option>
                      <option>Net 15</option>
                      <option>Due on receipt</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Tracking no.
                    </label>
                    <input
                      type="text"
                      name="TrackingNum"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 transition-all"
                      value={formData.TrackingNum}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                      Invoice date
                    </label>
                    <input
                      type="date"
                      name="TxnDate"
                      className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 transition-all"
                      value={formData.TxnDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 text-left">
                    Due date
                  </label>
                  <input
                    type="date"
                    name="DueDate"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 transition-all"
                    value={formData.DueDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Custom Multi-Input Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 pt-4 border-t border-gray-50">

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Invoice number
                  </label>
                  <input
                    type="text"
                    name="InvoiceNumber"
                    value={formData.InvoiceNumber}
                    onChange={handleInputChange}
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  />
                  <span className="text-[10px] text-gray-600">(hidden)</span>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Estimate Number
                  </label>
                  <input
                    type="text"
                    name="EstimateNumber"
                    value={formData.EstimateNumber}
                    onChange={handleInputChange}
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  />
                  <span className="text-[10px] text-gray-600">(hidden)</span>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Vendor Details
                  </label>
                  <input
                    type="text"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  />
                  <span className="text-[10px] text-gray-600">(hidden)</span>
                </div>


                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Vendor Number
                  </label>
                  <input
                    type="text"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Tops Shipping Systems, Corp
                  </label>
                  <input
                    type="text"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1 text-gray-600">
                    Cornelius Latino America
                  </label>
                  <input
                    type="date"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  />
                  <span className="text-[10px] text-gray-600">(hidden)</span>
                </div>


                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Type of currency
                  </label>
                  <input
                    type="text"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    PO Number
                  </label>
                  <input
                    type="text"
                    className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                  />
                  <span className="text-[10px] text-gray-600">(hidden)</span>
                </div>
              </div>


            {/* Line Items Table */}
            <div className="mt-10">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Product or service
              </h2>
              <div className="overflow-x-auto border border-gray-200 rounded-2xl">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-4 text-left font-semibold text-gray-600 w-12">
                        #
                      </th>
                      <th className="px-4 py-4 text-left font-semibold text-gray-600">
                        Product/Service
                      </th>
                      <th className="px-4 py-4 text-left font-semibold text-gray-600">
                        Description
                      </th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-600 w-24">
                        Qty
                      </th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-600 w-32">
                        Rate
                      </th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-600 w-32">
                        Amount
                      </th>
                      <th className="px-4 py-4 text-center font-semibold text-gray-600 w-16">
                        Tax
                      </th>
                      <th className="px-4 py-4 text-center font-semibold text-gray-600 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {formData.Line.map((line, index) => (
                      <tr key={index}>
                        <td className="px-4 py-4 text-gray-500">{index + 1}</td>
                        <td className="px-4 py-4">
                          <select
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 min-w-[150px] bg-white"
                            value={line.SalesItemLineDetail.ItemRef.value}
                            onChange={(e) =>
                              handleLineChange(index, "ItemRef", e.target.value)
                            }
                          >
                            <option value="">Select Product</option>
                            {products.map((p) => (
                              <option key={p.Id} value={p.Id}>
                                {p.Name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-4">
                          <input
                            type="text"
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 min-w-[150px]"
                            value={line.Description}
                            onChange={(e) =>
                              handleLineChange(
                                index,
                                "Description",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="px-4 py-4">
                          <input
                            type="number"
                            min="1"
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 min-w-[65px] text-right"
                            value={line.SalesItemLineDetail.Qty}
                            onChange={(e) =>
                              handleLineChange(index, "Qty", e.target.value)
                            }
                          />
                        </td>
                        <td className="px-4 py-4">
                          <input
                            type="number"
                            min="0"
                            className="h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 min-w-[100px] text-right"
                            value={line.SalesItemLineDetail.UnitPrice || ""}
                            onChange={(e) =>
                              handleLineChange(
                                index,
                                "UnitPrice",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="px-4 py-4 text-right font-medium">
                          ${line.Amount.toFixed(2)}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            className="size-4 accent-[#115B9D]"
                          />
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => removeLine(index)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end flex-wrap gap-2 mt-5">
                <button
                  type="button"
                  onClick={addLine}
                  className="px-5 py-2.5 text-sm font-medium text-[#115B9D] bg-blue-50 rounded-xl hover:bg-blue-100 transition-all border border-blue-100"
                >
                  Add product or service
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, Line: [], TotalAmt: 0 }))
                  }
                  className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                >
                  Clear all lines
                </button>
              </div>
            </div>

            {/* Bottom Section: Notes and Totals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-8 lg:gap-12">
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Customer payment options{" "}
                  </h3>
                  <textarea
                    rows="2"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    placeholder="Tell your customer how you want to get paid."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 text-gray-600">
                    Note to customer
                  </label>
                  <textarea
                    name="CustomerMemo"
                    rows="2"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.CustomerMemo}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 text-gray-600">
                    Internal customer notes (hidden)
                  </label>
                  <textarea
                    name="InternalNotes"
                    rows="2"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.InternalNotes}
                    onChange={handleInputChange}
                    placeholder="Add notes about this customer. This is only visible to you."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2 text-gray-600">
                    Memo on statement (hidden)
                  </label>
                  <textarea
                    name="PrivateNote"
                    rows="2"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.PrivateNote}
                    onChange={handleInputChange}
                    placeholder="This memo will not show up on your invoice, but will appear on the statement."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Attachments
                  </label>

                  <div className="border-2 border-dashed border-gray-200 rounded-xl text-center bg-gray-50 px-5 py-5">
                    {formData?.file ? (
                      <div className="flex items-center justify-center gap-2 relative">
                        <FaFile size={15} />
                        <span className="text-sm">File Uploaded</span>
                        <RxCross2
                          size={17}
                          className="hover:text-red-600 cursor-pointer"
                          onClick={() => setFormData({ ...formData, file: "" })}
                        />
                      </div>
                    ) : (
                      <label
                        htmlFor="dropzone-file"
                        className="text-[#115B9D] font-medium hover:underline cursor-pointer"
                      >
                        Add attachment
                        <input
                          id="dropzone-file"
                          type="file"
                          className="hidden"
                          onChange={(e) => uploadFile(e)}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50/50 rounded-2xl p-8 space-y-5">
                  {/* Subtotal */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium">Subtotal</span>
                    <span className="text-gray-900 font-semibold">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>

                  {/* Taxable subtotal */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium">
                      Taxable subtotal
                    </span>
                    <span className="text-gray-900 font-semibold">$0.00</span>
                  </div>

                  {/* Discount */}
                  <div className="flex justify-between items-center text-sm gap-4">
                    <span className="text-gray-500 font-medium shrink-0">
                      Discount
                    </span>
                    <div className="flex items-center gap-2 ml-auto">
                      <input
                        type="number"
                        min="0"
                        name="discount_value"
                        className="w-24 p-1.5 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 bg-white"
                        value={formData.discount_value}
                        onChange={handleInputChange}
                      />
                      <select
                        name="discount_type"
                        className="p-1.5 border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 bg-white"
                        value={formData.discount_type}
                        onChange={handleInputChange}
                      >
                        <option value="fixed">$</option>
                        <option value="percent">%</option>
                      </select>
                      <span className="text-gray-900 font-semibold w-16 text-right">
                        -${discountAmt.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Sales Tax */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium">
                      Sales tax{" "}
                      <svg
                        className="inline size-3.5 ml-1 text-gray-600"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-600 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        name="total_tax"
                        className="w-20 p-1 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 bg-white"
                        value={formData.total_tax}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Shipping */}
                  <div className="flex justify-between items-center text-sm pb-4 border-b">
                    <span className="text-gray-500 font-medium">Shipping</span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-600 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        name="total_shipping"
                        className="w-20 p-1 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 bg-white"
                        value={formData.total_shipping}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Invoice Total */}
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-gray-900">
                      Invoice total
                    </span>
                    <span className="text-2xl font-bold text-[#115B9D]">
                      ${invoiceTotal.toFixed(2)}
                    </span>
                  </div>

                  {/* Deposit */}
                  <div className="flex justify-between items-center pt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500">
                        Deposit
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        name="deposit_percent"
                        className="w-16 p-1 text-center border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 bg-white"
                        value={formData.deposit_percent}
                        onChange={handleInputChange}
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            deposit_percent:
                              Number(prev.deposit_percent) > 0 ? 0 : 50,
                          }))
                        }
                        className={`w-12 h-6 rounded-full relative transition-colors ${Number(formData.deposit_percent) > 0
                          ? "bg-[#115B9D]"
                          : "bg-gray-200"
                          }`}
                      >
                        <div
                          className={`absolute top-1 size-4 bg-white rounded-full shadow-sm transition-all ${Number(formData.deposit_percent) > 0
                            ? "right-1"
                            : "left-1"
                            }`}
                        />
                      </button>
                      <span className="text-gray-900 font-semibold">
                        ${depositAmt.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
                 <div className="flex gap-2 flex-wrap items-center justify-end border-t mt-12 pt-6">
                  <button
                    type="submit"
                    className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
                  >
                    Save
                  </button>
               
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
